/**
 * The five filter parameters of TMDXNC-175 AC1, and the selection they hold.
 *
 * Values are strings for every parameter, including crew, whose ids are
 * stringified at the facet boundary. Redux state must stay serialisable, which
 * is also why this is an array rather than a Set — the same reasoning as
 * `eventFilter`'s `hiddenKeys`. SpecRef: E010, E029
 *
 * **Selected, not hidden.** `eventFilter` models exclusion; this models
 * inclusion, because AC2 leaves visible only rows that match and AC5 restores
 * every row when the selection empties. SpecRef: E049
 */
export const FLEET_FILTER_PARAMETERS = [
  'case',
  'organ',
  'tail',
  'fbo',
  'crew',
] as const;

export type FleetFilterParameter = (typeof FLEET_FILTER_PARAMETERS)[number];

export type FleetFilterSelection = Record<FleetFilterParameter, string[]>;

export interface FleetFilterState {
  selected: FleetFilterSelection;
}

/**
 * A fresh empty selection.
 *
 * A factory, not a shared frozen constant: handing the same arrays to every
 * store and every test lets one mutation leak into all of them, and freezing
 * them instead needs `as unknown as string[]` casts to satisfy the mutable
 * `Record` this type declares.
 */
export const emptyFleetFilterSelection = (): FleetFilterSelection => ({
  case: [],
  organ: [],
  tail: [],
  fbo: [],
  crew: [],
});

/** Singular, for the header's active-filter groups. */
export const FLEET_FILTER_PARAMETER_LABEL: Record<
  FleetFilterParameter,
  string
> = {
  case: 'Case',
  organ: 'Organ',
  tail: 'Tail',
  fbo: 'FBO',
  crew: 'Crew',
};

/**
 * Plural, for the panel's column headings.
 *
 * `crew` differs from `FLEET_FILTER_PARAMETER_LABEL.crew` ('Crew') on
 * purpose: the header group and the panel column both render a
 * `role="group"` at the same time, and two groups sharing one accessible
 * name are indistinguishable to assistive tech.
 */
export const FLEET_FILTER_PARAMETER_LABEL_PLURAL: Record<
  FleetFilterParameter,
  string
> = {
  case: 'Cases',
  organ: 'Organs',
  tail: 'Tails',
  fbo: 'FBOs',
  crew: 'Crew members',
};
